(() => {
  if (window.__MERAI_CART_RUNTIME__) return;
  window.__MERAI_CART_RUNTIME__ = true;

  const MATCHES_VARIANT_ID = 63064826741107;
  const LIBRARY_VARIANT_ID = 63064832704883;
  const GIFT_VARIANT_IDS = new Set([MATCHES_VARIANT_ID, LIBRARY_VARIANT_ID]);
  const ELIGIBLE_PRODUCT_IDS = new Set([
    15429273158003,
    15429273387379,
    15429273616755,
    15429273878899,
    15429274009971
  ]);

  class MeraiCartRuntime {
    constructor() {
      this.timer = null;
      this.queue = Promise.resolve();
      this.lastCart = null;
      this.drawerObserver = null;
      this.stickyObserver = null;
      this.isApplyingSections = false;
      this.started = false;
      this.start = this.start.bind(this);
    }

    start() {
      if (this.started) return;
      this.started = true;
      this.bindEvents();
      this.observeDrawer();
      this.initStickyFooterGuard();
      this.schedule(0);
    }

    bindEvents() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
          if (event.source === 'merai-gifts') return;
          this.schedule(180);
        });
      }
      document.addEventListener('cart:updated', () => this.schedule(180));
      document.addEventListener('shopify:section:load', () => {
        this.initStickyFooterGuard();
        this.schedule(80);
      });
    }

    schedule(delay = 120) {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        this.queue = this.queue
          .then(() => this.reconcile())
          .catch((error) => console.warn('[Mèrai] Cart gift sync was skipped.', error));
      }, delay);
    }

    async reconcile() {
      this.setSyncing(true);
      try {
        let cart = await this.fetchCart();
        const candleCount = this.countEligibleCandles(cart);
        const desiredGiftId = candleCount >= 3
          ? LIBRARY_VARIANT_ID
          : candleCount === 2
            ? MATCHES_VARIANT_ID
            : null;

        const updates = {};
        let desiredGiftPresent = false;

        cart.items.forEach((item) => {
          const variantId = Number(item.variant_id);
          if (!GIFT_VARIANT_IDS.has(variantId)) return;

          if (!desiredGiftId || variantId !== desiredGiftId || desiredGiftPresent) {
            updates[item.key] = 0;
            return;
          }

          desiredGiftPresent = true;
          if (Number(item.quantity) !== 1) updates[item.key] = 1;
        });

        const sections = this.getSectionIds();
        let sectionPayload = null;

        if (Object.keys(updates).length) {
          sectionPayload = await this.postCart('/cart/update', {
            updates,
            sections,
            sections_url: window.location.pathname
          });
          cart = await this.fetchCart();
        }

        const desiredStillPresent = desiredGiftId && cart.items.some(
          (item) => Number(item.variant_id) === desiredGiftId
        );

        if (desiredGiftId && !desiredStillPresent) {
          sectionPayload = await this.postCart('/cart/add', {
            items: [{
              id: desiredGiftId,
              quantity: 1,
              properties: {
                _merai_gift: desiredGiftId === MATCHES_VARIANT_ID
                  ? 'signature-matches'
                  : 'scent-library'
              }
            }],
            sections,
            sections_url: window.location.pathname
          });
          cart = await this.fetchCart();
        }

        this.lastCart = cart;
        if (sectionPayload?.sections) this.renderNativeSections(sectionPayload.sections);
        this.renderStableState(cart);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'merai-gifts', cartData: cart });
        }
      } finally {
        this.setSyncing(false);
      }
    }

    countEligibleCandles(cart) {
      return cart.items.reduce((total, item) => {
        if (!ELIGIBLE_PRODUCT_IDS.has(Number(item.product_id))) return total;
        return total + Number(item.quantity || 0);
      }, 0);
    }

    visibleItemCount(cart) {
      return cart.items.reduce((total, item) => {
        return GIFT_VARIANT_IDS.has(Number(item.variant_id))
          ? total
          : total + Number(item.quantity || 0);
      }, 0);
    }

    renderStableState(cart) {
      this.renderProgress(cart);
      this.updateVisibleCounts(cart);
      this.enhanceGiftLines();
    }

    updateVisibleCounts(cart) {
      const count = this.visibleItemCount(cart);
      document.querySelectorAll('[data-cart-render="item_count"]').forEach((node) => {
        node.textContent = String(count);
      });
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], .cart-count-bubble .count').forEach((node) => {
        node.textContent = String(count);
      });
    }

    async fetchCart() {
      const routeConfig = typeof routes !== 'undefined' ? routes : window.routes;
      const response = await fetch(this.endpoint(routeConfig?.cart_url, '/cart'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
      return response.json();
    }

    async postCart(fallbackPath, body) {
      const routeConfig = typeof routes !== 'undefined' ? routes : window.routes;
      const routeMap = {
        '/cart/update': routeConfig?.cart_update_url,
        '/cart/add': routeConfig?.cart_add_url
      };
      const response = await fetch(this.endpoint(routeMap[fallbackPath], fallbackPath), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error(`Cart mutation failed with ${response.status}`);
      return response.json();
    }

    endpoint(routeValue, fallback) {
      const value = routeValue || fallback;
      return value.endsWith('.js') ? value : `${value}.js`;
    }

    getSectionIds() {
      const ids = ['cart-drawer', 'cart-icon-bubble'];
      const mainCart = document.getElementById('main-cart-items');
      if (mainCart?.dataset.id) ids.push(mainCart.dataset.id);
      return [...new Set(ids.filter(Boolean))];
    }

    renderNativeSections(sections) {
      this.isApplyingSections = true;
      try {
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.getSectionsToRender && drawer?.getSectionInnerHTML) {
          drawer.getSectionsToRender().forEach((section) => {
            const html = sections[section.id];
            if (!html) return;
            const target = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!target) return;
            target.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
          });
          drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
        }

        const mainCart = document.getElementById('main-cart-items');
        const mainSectionId = mainCart?.dataset.id;
        const mainHtml = mainSectionId ? sections[mainSectionId] : null;
        if (mainHtml) {
          const doc = new DOMParser().parseFromString(mainHtml, 'text/html');
          const sourceItems = doc.querySelector('#main-cart-items .js-contents');
          const targetItems = document.querySelector('#main-cart-items .js-contents');
          if (sourceItems && targetItems) targetItems.innerHTML = sourceItems.innerHTML;

          const sourceTotal = doc.querySelector('#main-cart-footer .cartTotal');
          const targetTotal = document.querySelector('#main-cart-footer .cartTotal');
          if (sourceTotal && targetTotal) targetTotal.innerHTML = sourceTotal.innerHTML;
        }
      } finally {
        window.setTimeout(() => { this.isApplyingSections = false; }, 60);
      }
    }

    renderProgress(cart) {
      const candleCount = this.countEligibleCandles(cart);
      const progress = candleCount >= 3 ? 100 : candleCount === 2 ? 66 : candleCount === 1 ? 33 : 0;
      const copy = this.progressCopy(candleCount);
      const markup = this.progressMarkup(copy, progress, candleCount);

      const drawer = document.querySelector('cart-drawer');
      const drawerHeader = drawer?.querySelector('.drawer__header, #CartDrawer .h4');
      if (drawerHeader) {
        let block = drawer.querySelector('[data-merai-gift-progress="drawer"]');
        if (!block) {
          block = document.createElement('div');
          block.dataset.meraiGiftProgress = 'drawer';
          drawerHeader.insertAdjacentElement('afterend', block);
        }
        block.className = 'merai-gift-progress merai-gift-progress--drawer';
        if (block.dataset.state !== String(candleCount)) {
          block.innerHTML = markup;
          block.dataset.state = String(candleCount);
        }
      }

      const mainCart = document.getElementById('main-cart-items');
      if (mainCart?.parentElement) {
        let block = document.querySelector('[data-merai-gift-progress="page"]');
        if (!block) {
          block = document.createElement('div');
          block.dataset.meraiGiftProgress = 'page';
          mainCart.parentElement.insertBefore(block, mainCart);
        }
        block.className = 'merai-gift-progress merai-gift-progress--page';
        if (block.dataset.state !== String(candleCount)) {
          block.innerHTML = markup;
          block.dataset.state = String(candleCount);
        }
      }
    }

    progressCopy(count) {
      if (count >= 3) return {
        title: 'The Scent Library is unlocked.',
        detail: 'It replaces Signature Matches and is added automatically.'
      };
      if (count === 2) return {
        title: 'Signature Matches unlocked.',
        detail: 'Add one more full-size candle to receive the Scent Library instead.'
      };
      if (count === 1) return {
        title: 'One more room unlocks a gift.',
        detail: 'Add a second full-size candle to receive Signature Matches.'
      };
      return {
        title: 'Compliments of the house.',
        detail: 'Two full-size candles unlock Signature Matches. Three unlock the Scent Library instead.'
      };
    }

    progressMarkup(copy, progress, count) {
      return `
        <div class="merai-gift-progress__copy" aria-live="polite">
          <span>COMPLIMENTS OF THE HOUSE</span>
          <strong>${copy.title}</strong>
          <p>${copy.detail}</p>
        </div>
        <div class="merai-gift-progress__rail" role="progressbar" aria-valuemin="0" aria-valuemax="3" aria-valuenow="${Math.min(count, 3)}" aria-label="Complimentary gift progress">
          <i style="width:${progress}%"></i>
          <b class="merai-gift-progress__marker merai-gift-progress__marker--matches" aria-hidden="true"></b>
          <b class="merai-gift-progress__marker merai-gift-progress__marker--library" aria-hidden="true"></b>
        </div>
        <div class="merai-gift-progress__labels" aria-hidden="true">
          <span>Signature Matches · 2</span>
          <span>Scent Library · 3</span>
        </div>
      `;
    }

    enhanceGiftLines() {
      document.querySelectorAll('.cart-item').forEach((line) => {
        const giftLink = line.querySelector(
          'a[href*="/products/signature-matches-gift"], a[href*="/products/scent-library-gift"]'
        );
        if (!giftLink) return;
        line.classList.add('merai-gift-line');

        const details = line.querySelector('.cart-item__details, .cart-item__title, .cart-item__info');
        if (details && !details.querySelector('.merai-gift-line__badge')) {
          const badge = document.createElement('span');
          badge.className = 'merai-gift-line__badge';
          badge.textContent = 'COMPLIMENTARY · ₹0';
          details.appendChild(badge);
        }
      });
    }

    setSyncing(syncing) {
      document.body.classList.toggle('merai-cart-syncing', syncing);
      document.querySelectorAll('[name="checkout"], .checkout, a[href*="/checkout"]').forEach((control) => {
        if (syncing) {
          control.setAttribute('aria-disabled', 'true');
          if ('disabled' in control) control.disabled = true;
        } else {
          control.removeAttribute('aria-disabled');
          if ('disabled' in control) control.disabled = false;
        }
      });
    }

    observeDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || this.drawerObserver) return;
      this.drawerObserver = new MutationObserver(() => {
        if (this.isApplyingSections || !this.lastCart) return;
        window.requestAnimationFrame(() => this.renderStableState(this.lastCart));
      });
      this.drawerObserver.observe(drawer, { childList: true, subtree: true });
    }

    initStickyFooterGuard() {
      const sticky = document.querySelector('sticky-cart.sticky-cart, .template-product .sticky-cart');
      const footer = document.querySelector('footer, .site-footer, .footer, [id^="shopify-section-footer"]');
      if (!sticky || !footer || sticky.dataset.meraiFooterGuard === 'true') return;
      sticky.dataset.meraiFooterGuard = 'true';

      const setHidden = (hidden) => {
        sticky.classList.toggle('merai-sticky-footer-visible', hidden);
        sticky.setAttribute('data-merai-footer-visible', String(hidden));
      };

      if ('IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(
          ([entry]) => setHidden(entry.isIntersecting),
          { threshold: 0.01 }
        );
        this.stickyObserver.observe(footer);
      }
    }
  }

  const runtime = new MeraiCartRuntime();
  window.MeraiCartRuntime = runtime;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runtime.start, { once: true });
  } else {
    runtime.start();
  }
})();